import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { toast } from 'react-hot-toast';
import {
  buildStoreSiteContext,
  extractRequestedOrderDate,
  fetchStoreSiteContext,
  formatRequestedOrderDate,
  getStoreOrderOrigin,
  isStoreOrderDeliveredStatus,
  isStoreOrderFromPos,
  reverseStoreOrderStock,
} from '../utils/storeLogistics';
import {
  reconcileStoreOrdersClientAccounts,
  syncStoreOrderClientAccount,
} from '../utils/storeOrderAccounting';
import { getSaleItemDisplayName } from '../utils/productLabels';
import { 
  Package, ArrowLeft, User, MapPin, Calendar, 
  CreditCard, Truck, CheckCircle, XCircle, Clock, 
  FileText, ExternalLink, RefreshCcw, Save, AlertCircle, Mail,
  Printer, Receipt, Download, Paperclip, AlertTriangle, MessageCircle, Edit2, X
} from 'lucide-react';

// Las notas del pedido guardan, ademas del texto del cliente, el link al
// comprobante ("COMPROBANTE: https://..."). Al editarlas se separa ese token
// para que el usuario no lo vea ni lo pueda borrar sin querer.
const COMPROBANTE_TOKEN_RE = /COMPROBANTE:\s*https?:\/\/[^\s]+/gi;

const splitOrderNotes = (notes) => {
  const raw = typeof notes === 'string' ? notes : '';
  const tokens = raw.match(COMPROBANTE_TOKEN_RE) || [];
  const visible = raw.replace(COMPROBANTE_TOKEN_RE, '').replace(/(\.\s*)+$/, '').trim();
  return { visible, tokens };
};

const joinOrderNotes = (visible, tokens) => {
  const parts = [String(visible || '').trim(), ...tokens].filter(Boolean);
  return parts.length ? parts.join('. ') : null;
};

const STATUS_COLORS = {
  recibido: 'bg-blue-100 text-blue-700 border-blue-200',
  en_preparacion: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  listo: 'bg-purple-100 text-purple-700 border-purple-200',
  en_camino: 'bg-orange-100 text-orange-700 border-orange-200',
  entregado: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  retirado: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelado: 'bg-rose-100 text-rose-700 border-rose-200',
};

const STATUS_LABELS = {
  recibido: 'Nuevo',
  en_preparacion: 'En Preparación',
  listo: 'Listo para Entrega',
  en_camino: 'En Camino',
  entregado: 'Entregado',
  retirado: 'Entregado',
  cancelado: 'Cancelado',
};

export default function PedidoDetalle() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [storeSiteContext, setStoreSiteContext] = useState(() => buildStoreSiteContext());
  
  const [currentStatus, setCurrentStatus] = useState('');
  const [saving, setSaving] = useState(false);

  // Estado para el Modal de Confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  const saveOrderFields = async (changes, successMessage) => {
    if (!order?.id) return false;
    setSavingContact(true);
    try {
      const { data, error } = await supabase
        .from('store_orders')
        .update(changes)
        .eq('id', order.id)
        .eq('organization_id', order.organization_id)
        .select('id, notes, customer_phone, customer_snapshot')
        .single();
      if (error) throw error;
      setOrder((prev) => ({ ...prev, ...data }));
      toast.success(successMessage);
      return true;
    } catch (error) {
      console.error(error);
      toast.error('No se pudo guardar el cambio en el pedido');
      return false;
    } finally {
      setSavingContact(false);
    }
  };

  const startPhoneEdit = () => {
    setPhoneDraft(order?.customer_phone || order?.customer_snapshot?.phone || '');
    setEditingPhone(true);
  };

  const savePhone = async () => {
    const nextPhone = String(phoneDraft || '').trim();
    if (nextPhone && nextPhone.replace(/\D/g, '').length < 6) {
      toast.error('El telefono parece incompleto');
      return;
    }
    const snapshot = order?.customer_snapshot || {};
    const previousPhone = order?.customer_phone || snapshot.phone || null;
    // "contact" es lo que se muestra y usa WhatsApp: si era el telefono (o estaba
    // vacio) pasa a ser el nuevo; si era otro dato (documento, mail) se respeta
    // salvo que ahora haya telefono.
    const contactWasPhone = !snapshot.contact || snapshot.contact === previousPhone;
    const nextContact = nextPhone
      || (contactWasPhone ? (snapshot.doc_number || null) : snapshot.contact);
    const ok = await saveOrderFields({
      customer_phone: nextPhone || null,
      customer_snapshot: { ...snapshot, phone: nextPhone || null, contact: nextContact },
    }, 'Telefono actualizado');
    if (ok) setEditingPhone(false);
  };

  const startNotesEdit = () => {
    setNotesDraft(splitOrderNotes(order?.notes).visible);
    setEditingNotes(true);
  };

  const saveNotes = async () => {
    const { tokens } = splitOrderNotes(order?.notes);
    const ok = await saveOrderFields({ notes: joinOrderNotes(notesDraft, tokens) }, 'Notas actualizadas');
    if (ok) setEditingNotes(false);
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line
  }, [id]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      
      let query = supabase
        .from('store_orders')
        .select(`
            *,
            sale:sales!store_orders_sale_id_fkey (
                id, total, payment_method, created_at, invoice_status, organization_id, site_id,
                client:clients ( id, full_name, email, phone, doc_number, address )
            )
        `);

      if (isUUID) {
          query = query.eq('id', id);
      } else {
          query = query.eq('tracking_code', id);
      }

      const { data, error } = await query.single();

      if (error) throw error;

      await reconcileStoreOrdersClientAccounts({
        orders: data ? [data] : [],
        organizationId: data?.organization_id,
      });

      setOrder(data);
      setCurrentStatus(data.status);
      setClientData(data.sale?.client); 

      if (data.organization_id) {
        const siteContextRes = await fetchStoreSiteContext({
          supabase,
          organizationId: data.organization_id,
        });

        if (!siteContextRes.error) {
          setStoreSiteContext(siteContextRes.data);
        }
      }

      if (data.sale_id) {
        const { data: itemsData } = await supabase
          .from('sale_items')
          .select('*')
          .eq('sale_id', data.sale_id);
        setItems(
          (itemsData || []).map((item) => ({
            ...item,
            product_name_snapshot: getSaleItemDisplayName(item),
          })),
        );
      }

    } catch (err) {
      console.error(err);
      toast.error("Error cargando pedido");
    } finally {
      setLoading(false);
    }
  };

  const registrarIngresoFondos = async (saleData, shippingCost) => {
      try {
          const orgId = saleData.organization_id;
          const costoEnvio = Number(shippingCost || 0);
          const metodo = saleData.payment_method;

          // Para pagos en Efectivo contraentrega, el repartidor ya descontó su comisión, 
          // el dinero que ingresa a la caja del local es el neto (Total - Envío).
          // Para pagos Transferencia, el cliente nos transfirió el total bruto.
          const montoTotal = metodo === 'Efectivo' ? Number(saleData.total) - costoEnvio : Number(saleData.total);

          // 1. Obtener Configuración de Tienda (Banco Default)
          const { data: config } = await supabase
              .from('store_config')
              .select('default_bank_id')
              .eq('organization_id', orgId)
              .maybeSingle();

          // 2. Si es Transferencia
          if (metodo === 'Transferencia' && config?.default_bank_id) {
              const bankId = config.default_bank_id;

              // A. Ingreso del Total (Venta + Envío cobrado)
              await supabase.from('movimientos_financieros').insert({
                  organization_id: orgId,
                  bank_id: bankId,
                  tipo: 'Ingreso',
                  categoria: 'Venta Online',
                  monto: montoTotal,
                  descripcion: `Cobro Pedido #${order.tracking_code}`,
                  fecha_operacion: new Date(),
                  conciliado: true,
                  entidad_tipo: 'sale',
                  entidad_id: saleData.id
              });

              // Actualizar saldo del banco (Suma total)
              const { data: bank } = await supabase.from('banks').select('current_balance').eq('id', bankId).single();
              const newBalance = (Number(bank?.current_balance) || 0) + montoTotal;
              await supabase.from('banks').update({ current_balance: newBalance }).eq('id', bankId);

              toast.success("Dinero acreditado en Banco correctamente", { icon: '🏦' });
          } 
          // 3. Si es Efectivo
          else if (metodo === 'Efectivo') {
              const { data: turno } = await supabase
                  .from('turnos_caja')
                  .select('id')
                  .eq('organization_id', orgId)
                  .eq('estado', 'Abierto')
                  .maybeSingle();

              if (turno) {
                  // A. Ingreso a Caja
                  await supabase.from('movimientos_caja').insert({
                      organization_id: orgId,
                      turno_id: turno.id,
                      tipo: 'Ingreso',
                      categoria: 'Venta Online',
                      monto: montoTotal,
                      descripcion: `Cobro Pedido #${order.tracking_code} (Contraentrega)`
                  });

                  toast.success("Dinero ingresado a la Caja actual", { icon: '💵' });
              } else {
                  // Sin turno: registrar como financiero genérico
                  await supabase.from('movimientos_financieros').insert({
                      organization_id: orgId,
                      tipo: 'Ingreso',
                      categoria: 'Venta Online Efectivo',
                      monto: montoTotal,
                      descripcion: `Cobro Pedido #${order.tracking_code} (Sin turno caja)`
                  });
              }
          }

      } catch (err) {
          console.error("Error registrando fondos:", err);
          toast.error("Error al registrar el ingreso de dinero.");
          throw err;
      }
  };

  const requestUpdateStatus = (newStatus) => {
      if (newStatus === order.status) return;

      // Un pedido cancelado es TERMINAL: des-cancelarlo no re-descuenta stock y
      // re-cancelarlo lo repondría de nuevo (stock inflado). Se bloquea la salida.
      if (String(order.status || '').toLowerCase() === 'cancelado') {
          toast.error('Un pedido cancelado no puede cambiar de estado.');
          return;
      }

      if (newStatus === 'entregado') {
          setPendingStatus(newStatus);
          setShowConfirmModal(true);
      } else {
          executeStatusUpdate(newStatus);
      }
  };

  const executeStatusUpdate = async (newStatus) => {
      setSaving(true);
      const toastId = toast.loading("Procesando...");

        try {
          const orderOrigin = getStoreOrderOrigin(order);
          const managesCommercialFlow = !isStoreOrderFromPos(order);
          const { error } = await supabase
            .from('store_orders')
            .update({ status: newStatus, organization_id: order.organization_id })
            .eq('id', order.id);

          if (error) throw error;

          const nextSaleStatus =
            isStoreOrderDeliveredStatus(newStatus)
              ? 'completed'
              : newStatus === 'cancelado'
                ? 'canceled'
                : 'pending';

          if (managesCommercialFlow && order.sale_id) {
            await supabase.from('sales').update({ status: nextSaleStatus }).eq('id', order.sale_id);
          }

          const nextStatusNormalized = String(newStatus || '').toLowerCase();

          if (managesCommercialFlow && order.sale_id) {
            const clientAccountSync = await syncStoreOrderClientAccount({
              orderId: order.id,
              saleId: order.sale_id,
              organizationId: order.organization_id,
              trackingCode: order.tracking_code,
              nextStatus: newStatus,
              shippingCost: order.shipping_cost || 0,
              orderOrigin,
            });

            if (nextStatusNormalized !== 'cancelado' && clientAccountSync?.reason === 'missing_client') {
              toast('Pedido entregado, pero no tiene cliente vinculado para cuenta corriente.', { icon: '!' });
            }
          }

          if (managesCommercialFlow && newStatus === 'cancelado' && order.sale_id) {
            const reverseRes = await reverseStoreOrderStock({
              supabase,
              organizationId: order.organization_id,
              order,
              siteContext: storeSiteContext,
            });

            if (reverseRes.error) {
              console.error('Error revirtiendo stock del pedido web:', reverseRes.error);
              toast.error('Pedido cancelado, pero no se pudo revertir el stock.');
            }
          }
          

          toast.success("Pedido actualizado", { id: toastId });
          setOrder(prev => ({ ...prev, status: newStatus }));
          setCurrentStatus(newStatus);

      } catch (e) {
          console.error(e);
          toast.error(e.message || "Error al actualizar", { id: toastId });
      } finally {
          setSaving(false);
          setShowConfirmModal(false); 
      }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(val || 0);

  const getProofUrl = () => {
      if (!order) return null;
      if (order.shipping_address && order.shipping_address.proof_url) {
          return order.shipping_address.proof_url;
      }
      if (order.notes && typeof order.notes === 'string' && order.notes.includes('COMPROBANTE:')) {
          const parts = order.notes.split('COMPROBANTE:');
          if (parts.length > 1) {
              const urlCandidate = parts[1].trim().split(' ')[0];
              if (urlCandidate.startsWith('http')) return urlCandidate;
          }
      }
      return null;
  };

  const proofUrl = getProofUrl();
  const isImage = proofUrl && /\.(jpeg|jpg|gif|png|webp|avif|bmp|svg)($|\?)/i.test(proofUrl);
  const isPdf = proofUrl && /\.pdf($|\?)/i.test(proofUrl);
  const requestedDate = extractRequestedOrderDate(order);
  const requestedDateLabel = formatRequestedOrderDate(requestedDate);
  const customerPhone = order?.customer_snapshot?.contact || clientData?.phone || null;
  const normalizedCustomerPhone = customerPhone
    ? customerPhone.replace(/\D/g, '').startsWith('54')
      ? customerPhone.replace(/\D/g, '')
      : `549${customerPhone.replace(/\D/g, '')}`
    : null;

  const handlePrintInternalTicket = () => {
    if (!order) return;

    const itemRows = items.length
      ? items.map((item) => `
          <tr>
            <td style="padding:8px 0;border-bottom:1px solid #e2e8f0;font-size:12px;font-weight:700;color:#0f172a;">${item.quantity}x ${getSaleItemDisplayName(item)}</td>
            <td style="padding:8px 0;border-bottom:1px solid #e2e8f0;font-size:12px;text-align:right;color:#334155;">${formatCurrency(item.unit_price * item.quantity)}</td>
          </tr>
        `).join('')
      : `
        <tr>
          <td colspan="2" style="padding:10px 0;color:#64748b;font-size:12px;">Sin items cargados</td>
        </tr>
      `;

    const notesText = (order.notes || '').replace(/COMPROBANTE:\s*https?:\/\/[^\s]+/gi, '').trim();
    const deliveryLabel = order.delivery_type === 'envio' ? 'Envio' : 'Retiro';
    const addressLabel = order.delivery_type === 'envio'
      ? [order.shipping_address?.address, order.shipping_address?.city].filter(Boolean).join(' - ')
      : 'Retira en el local';
    const requestedDatePrint = requestedDate ? `<div style="font-size:12px;color:#475569;margin-top:4px;">Pedido para: ${requestedDateLabel}</div>` : '';
    const printWindow = window.open('', '_blank', 'width=900,height=700');

    if (!printWindow) {
      toast.error('No se pudo abrir la ventana de impresión.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Pedido ${order.tracking_code}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #0f172a; margin: 24px; }
            .header { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:24px; }
            .brand { font-size:11px; text-transform:uppercase; letter-spacing:.18em; color:#64748b; font-weight:700; }
            .title { font-size:28px; font-weight:800; margin:6px 0 4px; }
            .status { display:inline-block; padding:6px 10px; border-radius:999px; background:#e2e8f0; font-size:11px; font-weight:700; text-transform:uppercase; }
            .grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:16px; margin-bottom:20px; }
            .card { border:1px solid #e2e8f0; border-radius:14px; padding:14px; }
            .label { font-size:10px; text-transform:uppercase; letter-spacing:.14em; color:#64748b; font-weight:700; margin-bottom:6px; }
            .value { font-size:14px; font-weight:700; }
            table { width:100%; border-collapse:collapse; margin-top:8px; }
            .totals { margin-top:18px; margin-left:auto; width:260px; }
            .totals-row { display:flex; justify-content:space-between; font-size:13px; margin-bottom:8px; }
            .totals-final { display:flex; justify-content:space-between; font-size:18px; font-weight:800; border-top:2px solid #cbd5e1; padding-top:10px; }
            .notes { margin-top:20px; border:1px solid #fde68a; background:#fffbeb; border-radius:14px; padding:14px; font-size:12px; }
            @media print { body { margin: 12px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="brand">Detalle interno del pedido</div>
              <div class="title">Pedido #${order.tracking_code}</div>
              <div style="font-size:12px;color:#475569;">${new Date(order.created_at).toLocaleString('es-AR')}</div>
            </div>
            <div class="status">${STATUS_LABELS[order.status] || order.status}</div>
          </div>

          <div class="grid">
            <div class="card">
              <div class="label">Cliente</div>
              <div class="value">${order.customer_snapshot?.name || 'Consumidor final'}</div>
              <div style="font-size:12px;color:#475569;margin-top:4px;">${customerPhone || 'Sin telefono'}</div>
              <div style="font-size:12px;color:#475569;margin-top:4px;">${clientData?.email || ''}</div>
            </div>
            <div class="card">
              <div class="label">Entrega</div>
              <div class="value">${deliveryLabel}</div>
              <div style="font-size:12px;color:#475569;margin-top:4px;">${addressLabel}</div>
              ${requestedDatePrint}
            </div>
            <div class="card">
              <div class="label">Pago</div>
              <div class="value">${order.sale?.payment_method || 'Sin definir'}</div>
              <div style="font-size:12px;color:#475569;margin-top:4px;">${order.sale?.invoice_status === 'facturado' ? 'Factura emitida' : 'Sin facturar'}</div>
            </div>
            <div class="card">
              <div class="label">Referencia</div>
              <div class="value">Venta ${order.sale?.id || '-'}</div>
              <div style="font-size:12px;color:#475569;margin-top:4px;">Estado: ${STATUS_LABELS[order.status] || order.status}</div>
            </div>
          </div>

          <div class="card">
            <div class="label">Items</div>
            <table>
              <tbody>${itemRows}</tbody>
            </table>
            <div class="totals">
              ${Number(order.shipping_cost || 0) > 0 ? `<div class="totals-row"><span>Envio</span><strong>${formatCurrency(order.shipping_cost)}</strong></div>` : ''}
              <div class="totals-final"><span>Total</span><span>${formatCurrency(order.sale?.total)}</span></div>
            </div>
          </div>

          ${notesText ? `<div class="notes"><div class="label" style="margin-bottom:8px;">Notas del cliente</div>${notesText}</div>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={40} />
      <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Cargando Pedido...</p>
    </div>
  );

  if (!order) return null;

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] px-3 py-4 sm:px-4 md:px-6 lg:px-8 font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3 sm:items-center">
            <button onClick={() => navigate('/sistema/pedidos-web')} title="Volver al listado de pedidos web" className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-400 shadow-sm transition-colors hover:text-[#51A7FD]">
                <ArrowLeft size={20} />
            </button>
            <div className="min-w-0">
                <h1 className="flex items-center gap-2 text-xl font-black text-slate-800 tracking-tight uppercase sm:text-2xl">
                    Pedido #{order.tracking_code}
                </h1>
                <p className="mt-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 sm:text-[11px]">
                    <Clock size={12}/> {new Date(order.created_at).toLocaleString('es-AR')}
                </p>
            </div>
        </div>

        <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm sm:flex-row sm:items-center">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 sm:px-4">
                <span className="text-[9px] font-black uppercase text-slate-400 tracking-widest block mb-0.5">Estado:</span>
                <span className={`text-xs font-black uppercase ${
                    isStoreOrderDeliveredStatus(order.status) ? 'text-emerald-600' : 
                    order.status === 'cancelado' ? 'text-rose-600' : 'text-blue-600'
                }`}>
                    {STATUS_LABELS[order.status]}
                </span>
            </div>
            
            <div className="hidden h-8 w-px bg-slate-200 sm:mx-1 sm:block"></div>

            <select 
                value={currentStatus}
                onChange={(e) => requestUpdateStatus(e.target.value)}
                disabled={saving}
                title="Cambiar el estado del pedido"
                className="w-full cursor-pointer rounded-xl border-r-8 border-transparent bg-[#0B2A36] px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-white outline-none transition-colors hover:bg-slate-800 sm:w-auto"
            >
                {Object.keys(STATUS_LABELS).map(key => (
                    <option key={key} value={key}>{STATUS_LABELS[key]}</option>
                ))}
            </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3 xl:items-start">
          
          {/* COLUMNA IZQUIERDA (2/3) */}
          <div className="space-y-4 xl:col-span-2">
              
              {/* KPIS DE RESUMEN */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
                  <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Venta</span>
                          <CreditCard size={14} className="text-emerald-500"/>
                      </div>
                      <div className="text-base font-black text-slate-900 sm:text-lg">{formatCurrency(order.sale?.total)}</div>
                  </div>
                  <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cliente</span>
                          <User size={14} className="text-[#51A7FD]"/>
                      </div>
                      <div className="text-sm font-bold text-slate-800 truncate" title={order.customer_snapshot?.name}>
                          {order.customer_snapshot?.name?.split(' ')[0] || 'Cliente'}
                      </div>
                      <div className="text-[10px] font-medium text-slate-500">{order.customer_snapshot?.contact || '-'}</div>
                  </div>
                  <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Entrega</span>
                          <Truck size={14} className="text-purple-500"/>
                      </div>
                      <div className="text-sm font-bold text-slate-800 uppercase">{order.delivery_type}</div>
                      <div className="text-[10px] font-medium text-slate-500 truncate" title={order.shipping_address?.address || 'Retiro'}>
                          {order.delivery_type === 'envio' ? order.tracking_code.slice(-3) : 'Mostrador'}
                      </div>
                  </div>
                  <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Tiempo</span>
                          <Clock size={14} className="text-slate-400"/>
                      </div>
                      <div className="text-sm font-bold text-slate-800">
                          {Math.floor((new Date() - new Date(order.created_at)) / 60000)} min
                      </div>
                      <div className="text-[9px] font-medium text-slate-500">Desde creación</div>
                  </div>
              </div>

              {/* DETALLES DE ENTREGA (MOVIDO ARRIBA) */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3 sm:px-5">
                      <MapPin size={16} className="text-slate-400" />
                      <span className="font-black text-slate-800 text-[11px] uppercase tracking-widest">Detalle de Entrega</span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 p-4 sm:p-5 lg:grid-cols-2">
                      <div>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Contacto</p>
                          <p className="font-bold text-slate-800 text-sm">{order.customer_snapshot?.name}</p>
                          {editingPhone ? (
                              <div className="mt-1 flex items-center gap-2">
                                  <input
                                      autoFocus
                                      type="tel"
                                      value={phoneDraft}
                                      onChange={(e) => setPhoneDraft(e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') savePhone(); if (e.key === 'Escape') setEditingPhone(false); }}
                                      placeholder="Telefono del cliente"
                                      disabled={savingContact}
                                      className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 outline-none focus:border-[#51A7FD]"
                                  />
                                  <button type="button" onClick={savePhone} disabled={savingContact} title="Guardar" className="rounded-lg bg-[#0B2A36] p-1.5 text-white disabled:opacity-50"><Save size={12} /></button>
                                  <button type="button" onClick={() => setEditingPhone(false)} disabled={savingContact} title="Cancelar" className="rounded-lg border border-slate-200 p-1.5 text-slate-500"><X size={12} /></button>
                              </div>
                          ) : (
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                              <p className="text-xs text-slate-500 font-medium">{order.customer_snapshot?.contact}</p>
                              <button type="button" onClick={startPhoneEdit} title="Editar telefono" className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#51A7FD]"><Edit2 size={11} /></button>
                              {normalizedCustomerPhone && (
                                  <a 
                                      href={`https://wa.me/${normalizedCustomerPhone}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      title="Contactar al cliente"
                                      className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-600 transition-colors hover:bg-emerald-100"
                                  >
                                      <MessageCircle size={10} /> WhatsApp
                                  </a>
                              )}
                          </div>
                          )}
                          {clientData?.email && <p className="text-xs text-slate-400 mt-1">{clientData.email}</p>}
                      </div>
                      {order.delivery_type === 'envio' && order.shipping_address && (
                          <div>
                              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Ubicación</p>
                              <p className="font-bold text-slate-800 text-sm">{order.shipping_address.address}</p>
                              <p className="text-xs text-slate-500 font-medium uppercase">{order.shipping_address.city}</p>
                              {requestedDate && (
                                  <div className="mt-2 rounded border border-sky-100 bg-sky-50 p-2 text-[10px] font-bold text-sky-700 uppercase">
                                      Pedido para: {requestedDateLabel}
                                  </div>
                              )}
                              {order.shipping_address.notes && (
                                  <div className="mt-2 rounded border border-yellow-100 bg-yellow-50 p-2 text-[10px] font-bold italic text-yellow-800">
                                      Nota: "{order.shipping_address.notes}"
                                  </div>
                              )}
                          </div>
                      )}
                      {order.delivery_type === 'retiro' && (
                          <div>
                              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Retiro por Sucursal</p>
                              <p className="font-bold text-slate-800 text-sm">El cliente retira en el local.</p>
                              {requestedDate && (
                                  <div className="mt-2 rounded border border-sky-100 bg-sky-50 p-2 text-[10px] font-bold text-sky-700 uppercase">
                                      Pedido para: {requestedDateLabel}
                                  </div>
                              )}
                          </div>
                      )}
                  </div>
              </div>

              {/* LISTA DE ITEMS */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3 sm:px-5">
                      <Package size={16} className="text-slate-400" />
                      <span className="font-black text-slate-800 text-[11px] uppercase tracking-widest">Items del Pedido</span>
                  </div>
                  <div className="divide-y divide-slate-50">
                      {items.map((item) => (
                          <div key={item.id} className="flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                              <div className="flex items-center gap-3">
                                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 text-sm font-black text-slate-500">
                                      {item.quantity}x
                                  </div>
                                  <div className="min-w-0">
                                      <p className="font-bold text-slate-700 text-sm uppercase">{getSaleItemDisplayName(item)}</p>
                                      <p className="text-[10px] font-bold text-slate-400 tracking-wider">UNIT: {formatCurrency(item.unit_price)}</p>
                                  </div>
                              </div>
                              <div className="text-left font-black text-slate-900 text-sm sm:text-right">
                                  {formatCurrency(item.unit_price * item.quantity)}
                              </div>
                          </div>
                      ))}
                  </div>
                  <div className="border-t border-slate-200 bg-slate-50 px-4 py-4 sm:px-5">
                      {/* Desglose de Totales */}
                      {order.shipping_cost > 0 && (
                          <div className="flex justify-end mb-2">
                              <div className="text-right">
                                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Envío</p>
                                  <p className="text-sm font-bold text-slate-600">{formatCurrency(order.shipping_cost)}</p>
                              </div>
                          </div>
                      )}
                      
                      <div className="flex justify-end">
                          <div className="text-right">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Final</p>
                              <p className="text-xl font-black text-slate-900 leading-none sm:text-2xl">{formatCurrency(order.sale?.total)}</p>
                          </div>
                      </div>
                  </div>
              </div>

              {/* TARJETA DE COMPROBANTE (MOVIDO ABAJO) */}
              {proofUrl && (
                  <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm overflow-hidden animate-in slide-in-from-bottom-4 ring-1 ring-emerald-50">
                      <div className="flex flex-col gap-2 border-b border-emerald-100 bg-emerald-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                          <div className="flex items-center gap-2">
                              <Paperclip size={16} className="text-emerald-600" />
                              <span className="font-black text-emerald-800 text-[11px] uppercase tracking-widest">Comprobante de Pago</span>
                          </div>
                          <a 
                            href={proofUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            title="Abrir el comprobante original en una nueva pestaña"
                            className="flex items-center gap-1 self-start rounded-lg border border-emerald-100 bg-white px-2 py-1 text-[9px] font-bold uppercase text-emerald-600 hover:underline"
                          >
                              Abrir Original <ExternalLink size={10} />
                          </a>
                      </div>
                      
                      <div className="flex flex-col items-center justify-center bg-slate-50/30 p-4 sm:p-5">
                          {isImage && (
                              <div className="mb-4 w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                                  <img 
                                    src={proofUrl} 
                                    alt="Comprobante" 
                                    className="w-full h-auto max-h-[500px] object-contain bg-slate-100" 
                                  />
                              </div>
                          )}
                          {isPdf && (
                              <div className="mb-4 h-[380px] w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden sm:h-[500px]">
                                  <iframe 
                                    src={`${proofUrl}#toolbar=0`} 
                                    className="w-full h-full"
                                    title="Comprobante PDF"
                                  />
                              </div>
                          )}
                          {!isImage && !isPdf && (
                              <div className="mb-4 flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white p-8">
                                  <FileText size={48} className="text-slate-300 mb-3" />
                                  <span className="text-xs font-bold text-slate-500 uppercase">Archivo no previsualizable</span>
                              </div>
                          )}
                          <a 
                            href={proofUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            title="Descargar el comprobante adjunto"
                            className="flex items-center gap-2 rounded-xl bg-[#0B2A36] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition-all hover:scale-105"
                          >
                              <Download size={14} /> Descargar Archivo
                          </a>
                      </div>
                  </div>
              )}

          </div>

          {/* COLUMNA DERECHA (1/3) - ACCIONES */}
          <div className="space-y-4 xl:sticky xl:top-4">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-3">
                      <FileText size={14} className="text-slate-400"/>
                      <span className="font-black text-slate-800 text-[10px] uppercase tracking-widest">Comprobante interno</span>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3 text-center">
                      {order.sale?.invoice_status === 'facturado' ? (
                          <>
                              <p className="font-black text-slate-800 text-[11px] uppercase tracking-widest">Ticket de control</p>
                              <p className="text-[9px] text-slate-400">Solo para seguimiento interno del pedido.</p>
                              {order.sale?.cae && <p className="text-[9px] text-slate-400">CAE: {order.sale.cae}</p>}
                          </>
                      ) : (
                          <>
                              <p className="font-black text-slate-800 text-[11px] uppercase tracking-widest">Ticket de control</p>
                              <p className="text-[9px] text-slate-400 px-3 leading-relaxed">
                                  Solo para seguimiento interno del pedido.
                              </p>
                          </>
                      )}
                  </div>
                  <button 
                    title="Imprimir Ticket interno del pedido"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B2A36] py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition-colors hover:bg-slate-800"
                    onClick={handlePrintInternalTicket}
                  >
                      <Printer size={14} /> Imprimir Ticket
                  </button>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5">
                  <div className="mb-3 flex items-center justify-between">
                      <span className="font-black text-slate-800 text-[11px] uppercase tracking-widest">Notas del Cliente</span>
                      {!editingNotes && (
                          <button type="button" onClick={startNotesEdit} title="Editar notas" className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#51A7FD]"><Edit2 size={12} /></button>
                      )}
                  </div>
                  {editingNotes ? (
                      <div className="space-y-2">
                          <textarea
                              autoFocus
                              rows={4}
                              value={notesDraft}
                              onChange={(e) => setNotesDraft(e.target.value)}
                              disabled={savingContact}
                              maxLength={1000}
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 outline-none focus:border-[#51A7FD]"
                          />
                          <div className="flex justify-end gap-2">
                              <button type="button" onClick={() => setEditingNotes(false)} disabled={savingContact} className="rounded-lg border border-slate-200 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500">Cancelar</button>
                              <button type="button" onClick={saveNotes} disabled={savingContact} className="rounded-lg bg-[#0B2A36] px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50">{savingContact ? 'Guardando...' : 'Guardar'}</button>
                          </div>
                      </div>
                  ) : splitOrderNotes(order.notes).visible ? (
                      <p className="rounded-xl border border-yellow-100 bg-yellow-50 p-3 text-xs italic text-slate-600">
                          "{splitOrderNotes(order.notes).visible}"
                      </p>
                  ) : (
                      <p className="text-[10px] text-slate-400 italic">Sin notas.</p>
                  )}
              </div>

              {/* WHATSAPP CLIENTE */}
              {normalizedCustomerPhone && (
                  <div className="mb-4 bg-white rounded-2xl border border-emerald-200 shadow-sm p-4 ring-1 ring-emerald-50 sm:p-5">
                      <span className="font-black text-emerald-800 text-[11px] uppercase tracking-widest block mb-1">WhatsApp del Cliente</span>
                      <p className="text-[10px] text-emerald-600 font-medium mb-2">{order.customer_snapshot?.name || 'Cliente'}</p>
                      <p className="text-[10px] text-slate-500 font-medium mb-4">{customerPhone}</p>
                      <a 
                          href={`https://wa.me/${normalizedCustomerPhone}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          title="Abrir conversaci?n con el cliente"
                          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#3FF2AA] py-2.5 text-[10px] font-black uppercase tracking-widest text-[#0B2A36] transition-all shadow-emerald-100 hover:shadow-lg"
                      >
                          <MessageCircle size={14} /> Contactar Cliente
                      </a>
                  </div>
              )}
          </div>
      </div>

      {/* MODAL DE CONFIRMACIÓN (INTERNO EN HTML) */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[1000] bg-[#0B2A36]/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="animate-in zoom-in-95 w-full max-w-sm rounded-[2rem] bg-white p-5 shadow-2xl sm:p-6">
                <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4 shadow-inner">
                        <AlertTriangle size={32} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800 uppercase leading-none mb-2">¿Confirmar Entrega?</h3>
                    <p className="text-xs text-slate-500 font-medium px-4 leading-relaxed mb-6">
                        Al marcar como <strong>Entregado</strong>, el dinero ({formatCurrency(order.sale?.total)}) ingresará automáticamente a tus finanzas (Caja o Banco según corresponda). 
                        {order.shipping_cost > 0 && <span className="block mt-2 text-rose-500 font-bold">Se registrará un gasto de envío por {formatCurrency(order.shipping_cost)}.</span>}
                    </p>
                    
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
                        <button 
                            onClick={() => setShowConfirmModal(false)}
                            title="Cancelar el cambio de estado"
                            className="flex-1 rounded-xl py-3 text-slate-500 font-bold uppercase text-[10px] tracking-widest hover:bg-slate-50 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button 
                            onClick={() => executeStatusUpdate(pendingStatus)}
                            title="Confirmar la entrega y registrar el ingreso"
                            className="flex-1 rounded-xl bg-emerald-500 py-3 text-white font-bold uppercase text-[10px] tracking-widest hover:bg-emerald-600 shadow-lg shadow-emerald-200 transition-all"
                        >
                            Confirmar
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}

    </div>
  );
}


